import clsx from "clsx";
import { FormProvider, useForm } from "react-hook-form";
import { Admin } from "types/auth";
import { TwoFactorSetupFormScheme } from "./scheme";
import { Button } from "components/Button";
import { useState } from "react";
import { authAPI } from "services/auth";
import InputField from "components/fields/InputField";
import { AxiosError } from "axios";

type Props = {
  admin: Admin;
};

export const TwoFactorSetup: React.FC<Props> = ({ admin }) => {
  const { isTwoFactorEnabled } = admin;
  const [isLoading, setIsLoading] = useState(false);
  const form = useForm<TwoFactorSetupFormScheme>({
    values: {
      code: "",
      qrCodeUrl: "",
    },
  });
  authAPI.getUser();
  const handleActivate2Fa = async () => {
    form.clearErrors();

    try {
      setIsLoading(true);
      const res = await authAPI.getQRCore();

      if (res.qrCodeUrl) {
        form.setValue("qrCodeUrl", res.qrCodeUrl);
      } else {
        form.setError("qrCodeUrl", {
          type: "manual",
          message: "Something went wrong",
        });
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      form.setValue("qrCodeUrl", "");

      if (error instanceof AxiosError) {
        form.setError("qrCodeUrl", {
          type: "manual",
          message: error.response.data.message || error.message,
        });
      } else if (error instanceof Error) {
        form.setError("qrCodeUrl", {
          type: "manual",
          message: error.message,
        });
      }
      console.warn(error);
    }
  };

  const handleSubmit = async (data: TwoFactorSetupFormScheme) => {
    form.clearErrors();

    try {
      setIsLoading(true);
      await authAPI.verify2FASetup(data.code);

      authAPI.logout();

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);

      if (error instanceof AxiosError) {
        form.setError("code", {
          type: "manual",
          message: error.response.data.message || error.message,
        });
      } else if (error instanceof Error) {
        form.setError("code", {
          type: "manual",
          message: error.message,
        });
      }
      console.warn(error);
    }
  };

  const qrCodeUrl = form.watch("qrCodeUrl");

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-[16px]">
        <h4>2FA status:</h4>
        <span
          className={clsx(
            "rounded-md px-1 text-white",
            isTwoFactorEnabled ? "bg-green-500" : "bg-red-500"
          )}
        >
          {isTwoFactorEnabled ? "Enabled" : "Disabled"}
        </span>
      </div>

      {!isTwoFactorEnabled && (
        <>
          <div className="flex flex-col gap-6">
            <Button onClick={handleActivate2Fa} disabled={isLoading}>
              {qrCodeUrl ? "Get new QR core" : "Turn on 2FA auth"}
            </Button>
          </div>

          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <FormProvider {...form}>
              {form.formState.errors.qrCodeUrl && (
                <span className="text-sm text-red-500 dark:text-red-400">
                  {form.formState.errors.qrCodeUrl.message}
                </span>
              )}

              {!isLoading && qrCodeUrl && (
                <>
                  <p className="mb-1 ml-1 text-center text-base text-gray-600">
                    Enter code to complete two-factor set-up
                  </p>

                  <div className="flex flex-col items-center justify-center">
                    <img src={qrCodeUrl} alt="QR Code" className="w-[300px]" />

                    <p className="text-sm">Use Google Authenticator</p>
                  </div>

                  <InputField
                    variant="auth"
                    extra="mb-3"
                    label="One time code*"
                    placeholder="_ _ _ _ _ "
                    id="secondStepCode"
                    type="text"
                    required
                    {...form.register("code", { required: true })}
                  />
                  <Button type="submit" disabled={isLoading}>
                    Confirm code
                  </Button>
                </>
              )}
            </FormProvider>
          </form>
        </>
      )}
    </div>
  );
};
